import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";
import { HEALTH_COLORS, HEALTH_LABELS } from "@/lib/constants";
import PortfolioStatDialog from "@/components/PortfolioStatDialog";
import { workloadHeat } from "@/lib/heat";
import { toast } from "sonner";
import {
  Loader2, LayoutDashboard, AlertTriangle, ShieldAlert, ClipboardList, CalendarClock,
  Presentation, ArrowUpRight, RefreshCw, Activity, FolderKanban, TrendingUp, TrendingDown, IdCard, Users, UserCircle2, Flame, Radar,
} from "lucide-react";

const money = (n) => n;

const FOLDER_COLORS = ["#0044FF", "#10B981", "#7C3AED", "#F59E0B", "#EC4899", "#0EA5E9", "#EF4444", "#14B8A6"];

export default function Portfolio() {
  const [data, setData] = useState(null);
  const [genBusy, setGenBusy] = useState(false);
  const [viewSummary, setViewSummary] = useState(null);
  const [statOpen, setStatOpen] = useState(null);
  const [groupByPm, setGroupByPm] = useState(() => localStorage.getItem("pf-group-pm") === "1");
  const [groupByProgram, setGroupByProgram] = useState(() => localStorage.getItem("pf-group-program") === "1");
  const nav = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/portfolio");
      setData(data);
    } catch (e) { setData({ overall: {}, projects: [], attention: {} }); }
  };
  useEffect(() => { load(); }, []);

  const generateAllDue = async () => {
    const due = (data?.projects || []).filter((p) => p.exec_is_due && p.task_count > 0);
    if (!due.length) { toast.info("No readouts due right now."); return; }
    setGenBusy(true);
    let ok = 0;
    for (const p of due) {
      try { await api.post(`/projects/${p.id}/ai/executive-summary`); ok += 1; }
      catch (e) { /* continue */ }
    }
    toast.success(`Generated ${ok} weekly readout${ok === 1 ? "" : "s"}`);
    setGenBusy(false);
    load();
  };

  const openReadout = async (p) => {
    try {
      const { data } = await api.get(`/projects/${p.id}/exec-summary`);
      if (!data.summary) { toast.info("No readout yet — generate one first."); return; }
      setViewSummary({ name: p.name, ...data });
    } catch (e) { toast.error("Could not load readout"); }
  };

  if (!data) return <Layout><div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div></Layout>;

  const o = data.overall || {};
  const pmGroups = (() => {
    const map = new Map();
    for (const p of data.projects) {
      const k = p.owner_name || "Unassigned";
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(p);
    }
    return [...map.entries()]
      .map(([name, rows]) => ({ name, rows, avg: Math.round(rows.reduce((s, r) => s + (r.avg_percent || 0), 0) / rows.length) }))
      .sort((a, b) => b.rows.length - a.rows.length || a.name.localeCompare(b.name));
  })();
  const tableRows = groupByProgram
    ? (() => {
        const map = new Map();
        for (const p of data.projects) {
          const k = p.program || "No program";
          if (!map.has(k)) map.set(k, []);
          map.get(k).push(p);
        }
        return [...map.entries()]
          .map(([name, rows]) => ({ name, rows, avg: Math.round(rows.reduce((s, r) => s + (r.avg_percent || 0), 0) / rows.length) }))
          .sort((a, b) => (a.name === "No program") - (b.name === "No program") || a.name.localeCompare(b.name))
          .flatMap((g) => [{ __group: g, __program: true }, ...g.rows]);
      })()
    : groupByPm ? pmGroups.flatMap((g) => [{ __group: g }, ...g.rows]) : data.projects;
  const healthData = ["blue", "green", "yellow", "red", "gray"]
    .map((k) => ({ key: k, name: HEALTH_LABELS[k], value: (o.health_mix || {})[k] || 0, color: HEALTH_COLORS[k] }))
    .filter((d) => d.value > 0);
  const att = data.attention || {};

  return (
    <Layout>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <p className="overline text-muted-foreground mb-2">Executive view</p>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight flex items-center gap-3"><LayoutDashboard className="h-9 w-9 text-primary" /> Portfolio</h1>
          <p className="text-muted-foreground mt-2">Health, progress, and weekly readouts across all {o.projects || 0} projects.</p>
        </div>
        <Button className="gap-2 h-11" onClick={generateAllDue} disabled={genBusy} data-testid="generate-all-readouts">
          {genBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Presentation className="h-4 w-4" />} Generate due readouts
        </Button>
      </div>

      {/* KPI roll-up */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-6" data-testid="portfolio-kpis">
        <Kpi label="Projects" value={o.projects || 0} onClick={() => setStatOpen("projects")} />
        <Kpi label="Avg progress" value={`${o.avg_percent || 0}%`} accent="#0044FF" onClick={() => setStatOpen("progress")} />
        <Kpi label="Off-track projects" value={o.off_track_projects || 0} accent={o.off_track_projects ? "#EF4444" : undefined} onClick={() => setStatOpen("off_track")} />
        <Kpi label="At-risk tasks" value={o.flagged_tasks || 0} accent={o.flagged_tasks ? "#F59E0B" : undefined} onClick={() => setStatOpen("flagged")} />
        <Kpi label="Open risks" value={o.open_risks || 0} accent={o.open_risks ? "#F59E0B" : undefined} onClick={() => setStatOpen("risks")} />
        <Kpi label="True-ups due" value={o.trueup_due_projects || 0} accent={o.trueup_due_projects ? "#F59E0B" : undefined} onClick={() => setStatOpen("trueups")} />
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        {/* health chart */}
        <div className="border border-border rounded-lg p-5">
          <h3 className="font-display font-bold text-sm flex items-center gap-2 mb-3"><Activity className="h-4 w-4 text-primary" /> Task health across portfolio</h3>
          {healthData.length === 0 ? <p className="text-xs text-muted-foreground py-16 text-center">No task data yet.</p> : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={healthData} dataKey="value" nameKey="name" innerRadius={48} outerRadius={82} paddingAngle={2} strokeWidth={0}>
                  {healthData.map((d) => <Cell key={d.key} fill={d.color} />)}
                </Pie>
                <Tooltip />
                <Legend iconType="circle" wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* attention list */}
        <div className="border border-border rounded-lg p-5 lg:col-span-2" data-testid="attention-list">
          <h3 className="font-display font-bold text-sm mb-3">Needs attention</h3>
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-4">
            <AttentionGroup icon={AlertTriangle} color="#EF4444" title="Off-track" items={att.off_track} nav={nav} suffix="red tasks" />
            <AttentionGroup icon={CalendarClock} color="#F59E0B" title="Overdue tasks" items={att.overdue} nav={nav} suffix="overdue" />
            <AttentionGroup icon={ShieldAlert} color="#F97316" title="Open risks" items={att.open_risks} nav={nav} suffix="risks" />
            <AttentionGroup icon={ClipboardList} color="#EF4444" title="Late action items" items={att.late_actions} nav={nav} suffix="late" />
            <AttentionGroup icon={Radar} color="#0EA5E9" title="On the Radar (parked)" items={att.radar || []} nav={nav} suffix="parked" />
          </div>
          {(att.flagged || []).length > 0 && (
            <div className="mt-4 pt-3 border-t border-border" data-testid="attention-flagged">
              <p className="text-xs font-bold flex items-center gap-1.5 mb-1.5" style={{ color: "#F59E0B" }}>
                <ShieldAlert className="h-3.5 w-3.5" /> At Risk / Jeopardy tasks ({att.flagged.length})
              </p>
              <div className="space-y-1">
                {att.flagged.slice(0, 8).map((f) => (
                  <button key={f.task_id} onClick={() => nav(`/projects/${f.project_id}?tab=plan`)} className="flex items-center gap-2 w-full text-xs text-muted-foreground hover:text-foreground group" data-testid={`flagged-task-${f.task_id}`}>
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded shrink-0" style={f.track_status === "jeopardy" ? { color: "#EF4444", background: "#EF44441a" } : { color: "#F59E0B", background: "#F59E0B1a" }}>
                      {f.track_status === "jeopardy" ? "Jeopardy" : "At Risk"}
                    </span>
                    <span className="truncate font-medium">{f.task_title}</span>
                    <span className="shrink-0 text-muted-foreground/70">· {f.project_name}</span>
                    {f.risk_title && <span className="ml-auto shrink-0 italic truncate max-w-[200px]">↳ {f.risk_title}</span>}
                    <ArrowUpRight className="h-3 w-3 shrink-0 opacity-0 group-hover:opacity-100" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Command center strip */}
      <div className="grid lg:grid-cols-3 gap-6 mb-8" data-testid="portfolio-command-center">
        <div className="border border-border rounded-lg p-5" data-testid="pf-due-week">
          <h3 className="font-display font-bold text-sm flex items-center gap-2 mb-3"><CalendarClock className="h-4 w-4 text-primary" /> Due this week <span className="text-xs text-muted-foreground font-normal">{(data.upcoming_tasks || []).length}</span></h3>
          {(data.upcoming_tasks || []).length === 0 ? <p className="text-xs text-muted-foreground py-4">Nothing due in the next 7 days.</p> : (
            <div className="space-y-1">
              {(data.upcoming_tasks || []).slice(0, 7).map((t) => (
                <button key={t.task_id} onClick={() => nav(`/projects/${t.project_id}?tab=plan`)} className="flex items-center gap-2 w-full text-xs hover:bg-secondary/40 rounded px-1 py-1 text-left" data-testid={`pf-upcoming-${t.task_id}`}>
                  <span className="truncate font-medium flex-1">{t.is_milestone ? "◆ " : ""}{t.title}</span>
                  <span className="text-muted-foreground shrink-0 truncate max-w-[110px]">{t.project_name}</span>
                  <span className="text-muted-foreground shrink-0">{t.end_date}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="border border-border rounded-lg p-5" data-testid="pf-resource-alerts">
          <h3 className="font-display font-bold text-sm flex items-center gap-2 mb-3"><TrendingUp className="h-4 w-4 text-primary" /> Resource alerts <span className="text-xs text-muted-foreground font-normal">{(data.resource_alerts || []).length}</span></h3>
          {(data.resource_alerts || []).length === 0 ? <p className="text-xs text-muted-foreground py-4">Everyone is pacing on schedule.</p> : (
            <div className="space-y-1.5">
              {(data.resource_alerts || []).slice(0, 7).map((r) => (
                <div key={r.person} className="flex items-center gap-2 text-xs px-1" data-testid={`pf-pace-${r.person.toLowerCase().replace(/\s/g, "-")}`}>
                  {r.trend === "ahead" ? <TrendingUp className="h-3.5 w-3.5 text-emerald-500 shrink-0" /> : <TrendingDown className="h-3.5 w-3.5 text-red-500 shrink-0" />}
                  <span className="truncate font-medium flex-1">{r.person}</span>
                  <span className={`font-bold shrink-0 ${r.trend === "ahead" ? "text-emerald-600" : "text-red-500"}`}>{r.delta > 0 ? "+" : ""}{r.delta}% {r.trend}</span>
                  <span className="text-muted-foreground shrink-0">({r.tasks} tasks)</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="border border-border rounded-lg p-5" data-testid="pf-cards-recap">
          <h3 className="font-display font-bold text-sm flex items-center gap-2 mb-3"><IdCard className="h-4 w-4 text-primary" /> Open cards
            <button onClick={() => nav("/cards?scope=portfolio")} className="ml-auto text-xs text-primary font-semibold hover:underline" data-testid="pf-cards-open">Open <ArrowUpRight className="h-3 w-3 inline" /></button>
          </h3>
          <p className="text-xs text-muted-foreground mb-2">
            <strong className="text-foreground">{data.cards_summary?.people || 0} people</strong> hold <strong className="text-foreground">{data.cards_summary?.open_items || 0} open items</strong>
            {data.cards_summary?.overdue_people ? <span className="text-red-500 font-semibold"> · {data.cards_summary.overdue_people} with overdue work</span> : ""}
            {data.cards_summary?.overloaded_people ? <span className="text-orange-600 font-semibold" data-testid="pf-overloaded-count"> · {data.cards_summary.overloaded_people} heavily loaded</span> : ""}.
          </p>
          <div className="space-y-1">
            {(data.cards_summary?.top || []).map((c) => {
              const heat = workloadHeat(c.open);
              return (
                <div key={c.person} className="flex items-center gap-2 text-xs px-1" data-testid={`pf-heat-${c.person.toLowerCase().replace(/\s+/g, "-")}`}>
                  <span className="inline-flex items-center gap-1 rounded-full px-1.5 py-px text-[10px] font-bold text-white shrink-0"
                        style={{ background: heat.bg }} title={`Workload heat: ${heat.label} — ${c.open} open item${c.open === 1 ? "" : "s"}`}>
                    <Flame className="h-2.5 w-2.5" /> {heat.label}
                  </span>
                  <span className="truncate font-medium flex-1">{c.person}</span>
                  <span className="text-muted-foreground shrink-0">{c.open} open</span>
                  {c.overdue > 0 && <span className="text-red-500 font-bold shrink-0">{c.overdue} overdue</span>}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* portfolio table */}
      <div className="flex items-center justify-end gap-2 mb-2">
        <button
          onClick={() => setGroupByProgram((v) => { const n = !v; localStorage.setItem("pf-group-program", n ? "1" : "0"); if (n) { setGroupByPm(false); localStorage.setItem("pf-group-pm", "0"); } return n; })}
          className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${groupByProgram ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground hover:bg-secondary/60"}`}
          data-testid="portfolio-group-program-toggle">
          <FolderKanban className="h-3.5 w-3.5" /> Group by Program
        </button>
        <button
          onClick={() => setGroupByPm((v) => { const n = !v; localStorage.setItem("pf-group-pm", n ? "1" : "0"); if (n) { setGroupByProgram(false); localStorage.setItem("pf-group-program", "0"); } return n; })}
          className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${groupByPm ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground hover:bg-secondary/60"}`}
          data-testid="portfolio-group-toggle">
          <Users className="h-3.5 w-3.5" /> Group by PM
        </button>
      </div>
      <div className="border border-border rounded-lg overflow-x-auto" data-testid="portfolio-table">
        <table className="w-full text-sm border-collapse min-w-[820px]">
          <thead>
            <tr className="border-b border-border bg-secondary/40 text-left">
              <th className="px-4 py-3 font-semibold">Project</th>
              <th className="px-3 py-3 font-semibold">Health</th>
              <th className="px-3 py-3 font-semibold">Progress</th>
              <th className="px-3 py-3 font-semibold text-center">Overdue</th>
              <th className="px-3 py-3 font-semibold text-center">At risk</th>
              <th className="px-3 py-3 font-semibold text-center">Risks</th>
              <th className="px-3 py-3 font-semibold text-center">Late actions</th>
              <th className="px-3 py-3 font-semibold text-center">Artifacts</th>
              <th className="px-3 py-3 font-semibold">Weekly readout</th>
              <th className="px-3 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {data.projects.length === 0 && <tr><td colSpan={10} className="px-4 py-8 text-center text-muted-foreground">No projects yet.</td></tr>}
            {tableRows.map((p, idx) => p.__group ? (
              <tr key={`grp-${p.__group.name}`} className="bg-secondary/60 border-b border-border" data-testid={`pm-group-${p.__group.name.toLowerCase().replace(/\s+/g, "-")}`}>
                <td colSpan={10} className="px-4 py-2">
                  <span className="text-xs font-bold inline-flex items-center gap-2">
                    {p.__program ? <FolderKanban className="h-4 w-4 text-primary" /> : <UserCircle2 className="h-4 w-4 text-primary" />} {p.__group.name}
                    <span className="text-muted-foreground font-semibold">· {p.__group.rows.length} project{p.__group.rows.length === 1 ? "" : "s"} · avg {p.__group.avg}% complete</span>
                  </span>
                </td>
              </tr>
            ) : (
              <tr key={p.id} className="border-b border-border/60 hover:bg-secondary/20" data-testid={`portfolio-row-${p.id}`}>
                <td className="px-4 py-3 font-medium">
                  <div className="flex items-center gap-3">
                    <span
                      className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0 shadow-sm"
                      style={{ background: FOLDER_COLORS[idx % FOLDER_COLORS.length], boxShadow: `0 4px 12px -2px ${FOLDER_COLORS[idx % FOLDER_COLORS.length]}66` }}
                    >
                      <FolderKanban className="h-[18px] w-[18px] text-white" strokeWidth={2.5} />
                    </span>
                    <div className="min-w-0">
                      <button onClick={() => nav(`/projects/${p.id}`)} className="hover:text-primary text-left block truncate">{p.name}</button>
                      {(p.owner_name || p.industry) && (
                        <span className="block text-[11px] text-muted-foreground" data-testid={`portfolio-owner-${p.id}`}>
                          {[p.owner_name && `PM: ${p.owner_name}`, p.industry].filter(Boolean).join(" · ")}
                        </span>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-3 py-3">
                  <span className="inline-flex items-center gap-1.5 text-xs font-semibold" style={{ color: HEALTH_COLORS[p.health] }}>
                    <span className="h-2.5 w-2.5 rounded-full" style={{ background: HEALTH_COLORS[p.health] }} /> {HEALTH_LABELS[p.health]}
                  </span>
                </td>
                <td className="px-3 py-3">
                  <div className="flex items-center gap-2">
                    <div className="h-1.5 w-20 rounded-full bg-secondary overflow-hidden"><div className="h-full rounded-full" style={{ width: `${p.avg_percent}%`, background: HEALTH_COLORS[p.health] }} /></div>
                    <span className="text-xs font-semibold">{p.avg_percent}%</span>
                  </div>
                </td>
                <td className="px-3 py-3 text-center">{p.overdue ? <span className="font-bold text-red-500">{p.overdue}</span> : <span className="text-muted-foreground/40">·</span>}</td>
                <td className="px-3 py-3 text-center" data-testid={`portfolio-flagged-${p.id}`}>{p.flagged_tasks ? <span className="font-bold text-amber-500">{p.flagged_tasks}</span> : <span className="text-muted-foreground/40">·</span>}</td>
                <td className="px-3 py-3 text-center">{p.open_risks ? <span className="font-bold text-amber-500">{p.open_risks}</span> : <span className="text-muted-foreground/40">·</span>}</td>
                <td className="px-3 py-3 text-center">{p.late_actions ? <span className="font-bold text-red-500">{p.late_actions}</span> : <span className="text-muted-foreground/40">·</span>}</td>
                <td className="px-3 py-3 text-center" data-testid={`portfolio-artifacts-${p.id}`}>{p.artifact_count ? <button onClick={() => nav(`/projects/${p.id}?tab=artifacts`)} className="font-bold text-primary hover:underline">{p.artifact_count}</button> : <span className="text-muted-foreground/40">·</span>}</td>
                <td className="px-3 py-3">
                  {p.exec_is_due
                    ? <span className="text-[11px] font-semibold px-2 py-0.5 rounded bg-amber-100 text-amber-700">Due</span>
                    : <span className="text-[11px] text-muted-foreground">{p.last_exec_at ? new Date(p.last_exec_at).toLocaleDateString() : "—"}</span>}
                </td>
                <td className="px-3 py-3 text-right">
                  <button onClick={() => openReadout(p)} className="text-xs text-primary font-semibold hover:underline inline-flex items-center gap-1" data-testid={`view-readout-${p.id}`}><Presentation className="h-3.5 w-3.5" /> Readout</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <PortfolioStatDialog kind={statOpen} data={data} onOpenChange={(v) => !v && setStatOpen(null)} />

      <PortfolioRaid />

      {viewSummary && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setViewSummary(null)} data-testid="readout-modal">
          <div className="bg-background rounded-lg max-w-2xl w-full max-h-[85vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-xl font-bold flex items-center gap-2"><Presentation className="h-5 w-5 text-primary" /> {viewSummary.name} — weekly readout</h3>
              <button onClick={() => setViewSummary(null)} className="text-muted-foreground hover:text-foreground text-sm">Close</button>
            </div>
            {viewSummary.generated_at && <p className="text-xs text-muted-foreground mb-3">Generated {new Date(viewSummary.generated_at).toLocaleString()}</p>}
            <div className="prose prose-sm max-w-none whitespace-pre-wrap text-sm">{viewSummary.summary}</div>
          </div>
        </div>
      )}
    </Layout>
  );
}

function Kpi({ label, value, accent, onClick }) {
  return (
    <button type="button" onClick={onClick} className="border border-border rounded-lg p-5 text-left w-full hover:border-foreground/30 hover:shadow-sm transition-all cursor-pointer" data-testid={`portfolio-kpi-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <p className="overline text-muted-foreground">{label}</p>
      <p className="font-display text-3xl font-extrabold tracking-tight mt-2" style={accent ? { color: accent } : {}}>{value}</p>
    </button>
  );
}

function AttentionGroup({ icon: Icon, color, title, items = [], nav, suffix }) {
  return (
    <div>
      <p className="text-xs font-bold flex items-center gap-1.5 mb-1.5" style={{ color }}><Icon className="h-3.5 w-3.5" /> {title} ({items.length})</p>
      {items.length === 0 ? <p className="text-xs text-muted-foreground">All clear 🎉</p> : (
        <div className="space-y-1">
          {items.slice(0, 5).map((i) => (
            <button key={i.id} onClick={() => nav(`/projects/${i.id}`)} className="flex items-center justify-between w-full text-xs text-muted-foreground hover:text-foreground group">
              <span className="truncate">{i.name}</span>
              <span className="shrink-0 ml-2 font-semibold">{i.count != null ? `${i.count} ${suffix}` : ""} <ArrowUpRight className="h-3 w-3 inline opacity-0 group-hover:opacity-100" /></span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function PortfolioRaid() {
  const nav = useNavigate();
  const [raid, setRaid] = useState(null);
  useEffect(() => { api.get("/portfolio/raid").then(({ data }) => setRaid(data)).catch(() => {}); }, []);
  if (!raid) return null;
  const s = raid.summary;
  const openDecisions = raid.decisions.filter((d) => d.status === "open").sort((a, b) => (b.blocking - a.blocking) || ((a.due_date || "9999") > (b.due_date || "9999") ? 1 : -1)).slice(0, 6);
  const topRisks = raid.risks.filter((r) => r.status !== "closed").sort((a, b) => (b.probability * b.impact) - (a.probability * a.impact)).slice(0, 6);
  const lateActions = raid.actions.filter((a) => a.status !== "done" && a.due_date && a.due_date < new Date().toISOString().slice(0, 10)).slice(0, 6);
  return (
    <div className="mt-8" data-testid="portfolio-raid">
      <div className="flex items-center gap-2 mb-3">
        <ShieldAlert className="h-5 w-5 text-primary" />
        <h2 className="font-display text-xl font-bold">RAID across the portfolio</h2>
        <span className="text-xs text-muted-foreground">· {s.open_risks} risks · {s.open_issues} issues · {s.open_actions} actions ({s.late_actions} late) · {s.open_decisions} decisions ({s.blocking_decisions} blocking)</span>
      </div>
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="border border-border rounded-lg p-4" data-testid="portfolio-raid-decisions">
          <p className="text-xs font-bold uppercase tracking-wider text-violet-600 mb-2">Open decisions</p>
          {openDecisions.length === 0 ? <p className="text-sm text-muted-foreground">None open.</p> : openDecisions.map((d) => (
            <button key={d.id} onClick={() => nav(`/projects/${d.project_id}?tab=raid`)} className="w-full text-left py-1.5 hover:bg-secondary/40 rounded px-1.5" data-testid={`praid-decision-${d.id}`}>
              <p className="text-sm truncate">{d.blocking && <span className="text-[9px] font-bold uppercase bg-violet-600 text-white rounded-full px-1.5 py-0.5 mr-1.5">Blocking</span>}{d.title}</p>
              <p className="text-xs text-muted-foreground">{d.project_name}{d.due_date ? ` · by ${d.due_date}` : ""}</p>
            </button>
          ))}
        </div>
        <div className="border border-border rounded-lg p-4" data-testid="portfolio-raid-risks">
          <p className="text-xs font-bold uppercase tracking-wider text-red-600 mb-2">Top risks</p>
          {topRisks.length === 0 ? <p className="text-sm text-muted-foreground">No open risks.</p> : topRisks.map((r) => (
            <button key={r.id} onClick={() => nav(`/projects/${r.project_id}?tab=raid`)} className="w-full text-left py-1.5 hover:bg-secondary/40 rounded px-1.5" data-testid={`praid-risk-${r.id}`}>
              <p className="text-sm truncate">{r.title}</p>
              <p className="text-xs text-muted-foreground">{r.project_name} · score {r.probability * r.impact}</p>
            </button>
          ))}
        </div>
        <div className="border border-border rounded-lg p-4" data-testid="portfolio-raid-actions">
          <p className="text-xs font-bold uppercase tracking-wider text-amber-600 mb-2">Late action items</p>
          {lateActions.length === 0 ? <p className="text-sm text-muted-foreground">Nothing late.</p> : lateActions.map((a) => (
            <button key={a.id} onClick={() => nav(`/projects/${a.project_id}?tab=raid`)} className="w-full text-left py-1.5 hover:bg-secondary/40 rounded px-1.5" data-testid={`praid-action-${a.id}`}>
              <p className="text-sm truncate">{a.title}</p>
              <p className="text-xs text-muted-foreground">{a.project_name} · was due {a.due_date}</p>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
